<script>
  // Prefix for the ids that tie each field to its visible label.
  const fid = `f${Math.random().toString(36).slice(2, 8)}`;
  import { createEventDispatcher } from 'svelte';
  import { Save, X as XIcon, AlertTriangle } from '../lib/icons/index.js';
  import { t, tImmediate } from '../i18n/index.js';
  import { companyInvoicesApi } from '../stores/api.js';
  import ErrorBanner from './ErrorBanner.svelte';
  import SuccessBanner from './SuccessBanner.svelte';
  import InfoHint from './InfoHint.svelte';
  import LineItemsEditor, { lineItemFromStored } from './LineItemsEditor.svelte';
  import InvoiceTotalsPreview from './InvoiceTotalsPreview.svelte';
  import { computeInvoiceTotals, submittedLineItems } from '../lib/totals.js';
  import { invoiceWarningNotices } from '../lib/warnings.js';
  import { classifyCorrectionIssues, buildCorrectionPagamento } from '../lib/correction.js';
  import { localizedError } from '../lib/labels.js';

  /**
   * Correcting an invoice the Sistema di Interscambio rejected.
   *
   * A scarto means the document was never issued: the number was not consumed
   * and the same invoice may be transmitted again once the cause is fixed. The
   * API has had `PATCH /companies/:id/invoices/:id` for that since v0.38.0, but
   * only a shell or an API client could reach it — from the browser the only
   * button was "reopen", which regenerates the XML from the stored metadata and
   * therefore answers 400 for ever on exactly the invoices that need fixing.
   * This form is the missing half.
   *
   * Three rules it keeps, and each of them is somebody's bad afternoon:
   *   - nothing is transmitted here. Saving rewrites the stored data; putting
   *     the document back on the wire stays a separate, deliberate act.
   *   - a partial correction is savable, because the endpoint deliberately
   *     warns rather than refuses on a document that is still incomplete. A fix
   *     that had to be total before it could be stored is one more door that
   *     locks from the outside.
   *   - the figures come from lib/totals.js and the rows from the same editor
   *     the creation screen uses. A second money pipeline is how the total on
   *     screen and the total in the XML end up a cent apart, which is the
   *     scarto (00423) this form exists to end.
   */
  export let companyId;
  export let invoiceId;
  /** The invoice as GET /invoices/:id returned it, metadata included. */
  export let invoice = null;
  /** `details` of the 400 the last reopen answered, already in Italian. */
  export let reopenIssues = [];
  /** The NotificaScarto row, when the archive holds one. */
  export let scartoNotification = null;

  const dispatch = createEventDispatcher();

  const TIPO_DOCUMENTO_CODES = ['TD01', 'TD02', 'TD03', 'TD04', 'TD05', 'TD06', 'TD24', 'TD25'];
  const MODALITA_CODES = ['MP01', 'MP05', 'MP08', 'MP12'];
  const CONDIZIONI_CODES = ['TP01', 'TP02', 'TP03'];
  const ESIGIBILITA_CODES = ['I', 'D', 'S'];

  let form = null;
  /**
   * The `pagamento` block exactly as it is stored. Kept verbatim so a field
   * this form does not show — ABI, BIC, the beneficiary — survives a
   * correction instead of being dropped by it.
   */
  let storedPagamento = null;
  /** The document as it was loaded — what "changed" is measured against. */
  let baseline = '';
  let saving = false;
  let saveError = null;
  let savedMessage = '';
  let warningNotices = [];
  /** The id the form was built from, so a reload does not discard typing. */
  let seededFrom = null;

  $: if (invoice && invoice.id !== seededFrom) seed(invoice);

  function seed(inv) {
    const metadata = inv.metadata || {};
    const datiGenerali = metadata.datiGenerali || {};
    const pagamento = metadata.pagamento || null;
    form = {
      invoiceDate: inv.invoiceDate ? String(inv.invoiceDate).slice(0, 10) : '',
      tipoDocumento: inv.tipoDocumento || datiGenerali.tipoDocumento || 'TD01',
      causale: datiGenerali.causale || '',
      codiceDestinatario: inv.codiceDestinatario || '',
      pecDestinatario: inv.pecDestinatario || '',
      esigibilitaIVA: metadata.esigibilitaIVA || datiGenerali.esigibilitaIVA || '',
      lineItems: (metadata.lineItems || []).map(lineItemFromStored),
      pagamento: {
        condizioni: pagamento?.condizioni || '',
        modalitaPagamento: pagamento?.modalitaPagamento || '',
        importoPagamento: pagamento?.importoPagamento != null ? String(pagamento.importoPagamento) : '',
        dataScadenza: pagamento?.dataScadenza ? String(pagamento.dataScadenza).slice(0, 10) : '',
        giorniTerminiPagamento: pagamento?.giorniTerminiPagamento != null ? String(pagamento.giorniTerminiPagamento) : '',
        iban: pagamento?.iban || '',
        dettagli: (pagamento?.dettagli || []).map((d) => ({
          modalitaPagamento: d?.modalitaPagamento || '',
          importoPagamento: d?.importoPagamento != null ? String(d.importoPagamento) : '',
          dataScadenza: d?.dataScadenza ? String(d.dataScadenza).slice(0, 10) : '',
        })),
      },
    };
    storedPagamento = pagamento;
    baseline = JSON.stringify(form);
    seededFrom = inv.id;
    savedMessage = '';
    warningNotices = [];
    saveError = null;
  }

  // The document-level code the riepiloghi will be grouped by, exactly as the
  // endpoint resolves it — see lib/totals.js.
  $: totals = form
    ? computeInvoiceTotals(form.lineItems, { esigibilitaIVA: form.esigibilitaIVA })
    : computeInvoiceTotals([]);

  $: classified = classifyCorrectionIssues(reopenIssues);

  /** Instalment rows carrying no ModalitaPagamento — the v0.38.0 refusal. */
  $: modalitaMissing = form
    ? form.pagamento.dettagli
        .map((d, i) => (!d.modalitaPagamento && !form.pagamento.modalitaPagamento ? i + 1 : 0))
        .filter(Boolean)
    : [];

  /** Options for a ModalitaPagamento select, keeping whatever is already set. */
  function modalitaOptions(current) {
    return current && !MODALITA_CODES.includes(current)
      ? [...MODALITA_CODES, current] : MODALITA_CODES;
  }

  function tipoOptions(current) {
    return current && !TIPO_DOCUMENTO_CODES.includes(current)
      ? [...TIPO_DOCUMENTO_CODES, current] : TIPO_DOCUMENTO_CODES;
  }

  /**
   * Only what the operator actually changed.
   *
   * The endpoint merges onto what is stored, so sending the untouched fields
   * back would be re-declaring every figure of a fiscal document on every save
   * — and re-declaring it in this form's own formatting.
   */
  function buildPayload() {
    const before = JSON.parse(baseline);
    const payload = {};
    for (const field of ['invoiceDate', 'tipoDocumento', 'causale', 'codiceDestinatario', 'pecDestinatario', 'esigibilitaIVA']) {
      if (form[field] === before[field]) continue;
      // A date can be corrected but not removed: `new Date('')` is not a date,
      // and an invoice whose Data is unreadable is refused all over again.
      if (field === 'invoiceDate' && !form.invoiceDate) continue;
      payload[field] = form[field];
    }
    if (JSON.stringify(form.lineItems) !== JSON.stringify(before.lineItems)) {
      payload.lineItems = submittedLineItems(form.lineItems);
    }
    if (JSON.stringify(form.pagamento) !== JSON.stringify(before.pagamento)) {
      payload.pagamento = buildCorrectionPagamento(form.pagamento, storedPagamento);
    }
    return payload;
  }

  // `baseline` is named on purpose: it is what buildPayload() compares against,
  // and without it here the counter would keep showing unsaved changes after a
  // save had already stored them.
  $: changedFields = form && baseline ? Object.keys(buildPayload()) : [];

  async function save() {
    saveError = null;
    savedMessage = '';
    warningNotices = [];
    const payload = buildPayload();
    if (Object.keys(payload).length === 0) {
      saveError = localizedError(tImmediate('invoices.correction.noChanges'));
      return;
    }
    saving = true;
    try {
      const result = await companyInvoicesApi.correct(companyId, invoiceId, payload);
      savedMessage = tImmediate('invoices.correction.saved');
      warningNotices = invoiceWarningNotices(result?.warnings);
      // What is on screen is now what is stored, so a second save sends only
      // the next round of changes. The form is deliberately *not* re-seeded
      // from the reloaded invoice: that would take the confirmation and the
      // warnings off the screen the instant they appeared.
      baseline = JSON.stringify(form);
      dispatch('saved');
    } catch (err) {
      saveError = err;
    } finally {
      saving = false;
    }
  }

  const inputCls = 'input';
  const inputStyle = '';
  const labelCls = 'block eyebrow mb-1';
  const labelStyle = 'color: var(--color-text-muted);';

  function formatDate(value) {
    if (!value) return '';
    return new Date(value).toLocaleString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  }
</script>

{#if form}
  <div class="card p-6 mb-6" id="invoice-correction">
    <div class="flex items-start justify-between gap-4 mb-2">
      <h2 class="eyebrow eyebrow-lg" style="color: var(--color-primary);">
        {$t('invoices.correction.title')}
      </h2>
      <button type="button" class="text-muted hover:text-action-strong" on:click={() => dispatch('close')}
        title={$t('invoices.correction.close')} aria-label={$t('invoices.correction.close')}>
        <XIcon size={18} />
      </button>
    </div>

    <p class="text-sm measure" style="color: var(--color-text-body);">{$t('invoices.correction.intro')}</p>
    <p class="text-sm measure mt-2" style="color: var(--color-text-body);">{$t('invoices.correction.keepsNumber')}</p>
    <p class="text-sm measure mt-2" style="color: var(--color-text-body);">{$t('invoices.correction.nothingSent')}</p>
    <p class="text-xs measure mt-2" style="color: var(--color-text-muted);">{$t('invoices.correction.partial')}</p>

    <!-- Perché SdI l'ha scartata. La notifica di scarto è l'unica fonte di
         verità sul motivo, e finché il suo contenuto non è leggibile a schermo
         va detto dov'è, invece di lasciar credere che il motivo sia sparito. -->
    <div class="mt-4 border-t pt-3" style="border-color: var(--color-tint-soft);">
      <h3 class="eyebrow" style="color: var(--color-text-muted);">
        {$t('invoices.correction.scartoReasonTitle')}
      </h3>
      {#if scartoNotification}
        <p class="text-sm mt-1 font-mono" style="color: var(--color-text-body);">
          {$t('invoices.correction.scartoReceivedOn', {
            data: formatDate(scartoNotification.sdiTimestamp || scartoNotification.createdAt),
            file: scartoNotification.nomeFile || '—',
          })}
        </p>
      {/if}
      <p class="text-xs measure mt-1" style="color: var(--color-text-muted);">
        {$t('invoices.correction.scartoReasonWhere')}
      </p>
    </div>

    <!-- Quello che blocca la rimessa in coda: gli stessi controlli che hanno
         rifiutato l'ultimo tentativo, ripetuti qui sotto accanto ai campi. -->
    {#if classified.all.length > 0}
      <div class="mt-4 border-t pt-3" style="border-color: var(--color-tint-soft);">
        <h3 class="eyebrow flex items-center gap-2 text-warning">
          <AlertTriangle size={14} /> {$t('invoices.correction.issuesTitle', { n: classified.all.length })}
        </h3>
        <p class="text-xs measure mt-1" style="color: var(--color-text-muted);">{$t('invoices.correction.issuesIntro')}</p>
        {#each classified.general as issue}
          <p class="text-xs measure mt-2 text-warning">{issue}</p>
        {/each}
      </div>
    {/if}

    <ErrorBanner error={saveError} title={$t('invoices.correction.errorTitle')} />
    <SuccessBanner message={savedMessage} />

    <!-- Salvato, ma il documento non è ancora completo: è esattamente il caso
         che l'endpoint segnala invece di rifiutare. -->
    {#if warningNotices.length > 0}
      <div class="my-4 p-3 border-w text-sm" style="border-color: var(--color-warning); color: var(--color-text-body);" role="status">
        <p class="font-bold text-warning">{$t('invoices.correction.warningsTitle')}</p>
        {#each warningNotices as notice}
          <p class="mt-1 measure">{notice.text}</p>
          {#if notice.detail}<p class="mt-1 text-xs measure" style="color: var(--color-text-muted);">{notice.detail}</p>{/if}
        {/each}
      </div>
    {/if}

    <form on:submit|preventDefault={save} class="mt-4">
      <h3 class="eyebrow mb-2" style="color: var(--color-text-muted);">
        {$t('invoices.correction.sections.document')}
      </h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label class="block">
          <span id="{fid}-1" class={labelCls} style={labelStyle}>{$t('invoiceCreate.fields.date')}</span>
          <input aria-labelledby="{fid}-1" type="date" bind:value={form.invoiceDate} class={inputCls} style={inputStyle} />
          {#each classified.fields.invoiceDate || [] as issue}
            <p class="mt-1 text-xs measure text-warning" role="status">{issue}</p>
          {/each}
        </label>
        <label class="block">
          <span id="{fid}-2" class={labelCls} style={labelStyle}>{$t('invoiceCreate.fields.tipoDocumento')}</span>
          <select aria-labelledby="{fid}-2" bind:value={form.tipoDocumento} class={inputCls} style={inputStyle}>
            {#each tipoOptions(form.tipoDocumento) as code}
              <option value={code}>{code} - {$t(`invoiceCreate.tipiDocumento.${code}`, undefined, code)}</option>
            {/each}
          </select>
          {#each classified.fields.tipoDocumento || [] as issue}
            <p class="mt-1 text-xs measure text-warning" role="status">{issue}</p>
          {/each}
        </label>
        <label class="block">
          <span id="{fid}-3" class={labelCls} style={labelStyle}>{$t('invoiceCreate.fields.causale')}</span>
          <input aria-labelledby="{fid}-3" bind:value={form.causale} class={inputCls} style={inputStyle} placeholder={$t('invoiceCreate.fields.causalePlaceholder')} />
        </label>
        <label class="block">
          <span id="{fid}-4" class="{labelCls} inline-flex items-center gap-1" style={labelStyle}>
            {$t('invoiceCreate.fields.codiceDestinatario')}<InfoHint text={$t('invoices.correction.codiceDestinatarioHint')} />
          </span>
          <input aria-labelledby="{fid}-4" bind:value={form.codiceDestinatario} maxlength="7" class="{inputCls} font-mono" style={inputStyle} />
          {#each classified.fields.codiceDestinatario || [] as issue}
            <p class="mt-1 text-xs measure text-warning" role="status">{issue}</p>
          {/each}
        </label>
        <label class="block">
          <span id="{fid}-5" class={labelCls} style={labelStyle}>{$t('invoiceCreate.fields.pecDestinatario')}</span>
          <input aria-labelledby="{fid}-5" type="email" bind:value={form.pecDestinatario} class={inputCls} style={inputStyle} />
          {#each classified.fields.pecDestinatario || [] as issue}
            <p class="mt-1 text-xs measure text-warning" role="status">{issue}</p>
          {/each}
        </label>
        <label class="block md:col-span-2">
          <span id="{fid}-6" class="{labelCls} inline-flex items-center gap-1" style={labelStyle}>
            {$t('invoiceCreate.fields.esigibilita')}<InfoHint text={$t('invoiceCreate.esigibilita.hint')} />
          </span>
          <select aria-labelledby="{fid}-6" bind:value={form.esigibilitaIVA} class={inputCls} style={inputStyle}>
            <option value="">{$t('invoiceCreate.esigibilita.unset')}</option>
            {#each ESIGIBILITA_CODES as code}
              <option value={code}>{$t(`invoiceCreate.esigibilita.options.${code}`)}</option>
            {/each}
          </select>
          {#if form.esigibilitaIVA}
            <p class="mt-2 text-xs measure" style="color: var(--color-text-muted);">
              {$t(`invoiceCreate.esigibilita.explain.${form.esigibilitaIVA}`)}
            </p>
          {/if}
          {#if invoice?.formatoTrasmissione === 'FPA12' && !form.esigibilitaIVA}
            <p class="mt-2 text-xs measure text-warning" role="status">
              {$t('invoiceCreate.esigibilita.paWarning')}
            </p>
          {/if}
          {#each classified.fields.esigibilitaIVA || [] as issue}
            <p class="mt-1 text-xs measure text-warning" role="status">{issue}</p>
          {/each}
        </label>
      </div>

      <h3 class="eyebrow mt-6 mb-2" style="color: var(--color-text-muted);">
        {$t('invoiceCreate.sections.lineItems')}
      </h3>
      {#each classified.fields.lineItems || [] as issue}
        <p class="mb-2 text-xs measure text-warning" role="status">{issue}</p>
      {/each}
      <LineItemsEditor bind:lineItems={form.lineItems} {totals} issuesByLine={classified.lines} />

      <InvoiceTotalsPreview {totals} issues={classified.fields.totalAmount || []}
        note={$t('invoices.correction.totalNote')} />

      <h3 class="eyebrow mt-6 mb-2" style="color: var(--color-text-muted);">
        {$t('invoiceCreate.sections.payment')}
      </h3>
      <p class="text-xs measure mb-3" style="color: var(--color-text-muted);">{$t('invoices.correction.paymentHelp')}</p>
      {#each classified.fields.pagamento || [] as issue}
        <p class="mb-2 text-xs measure text-warning" role="status">{issue}</p>
      {/each}

      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <label class="block">
          <span id="{fid}-7" class={labelCls} style={labelStyle}>{$t('invoiceCreate.fields.conditions')}</span>
          <select aria-labelledby="{fid}-7" bind:value={form.pagamento.condizioni} class={inputCls} style={inputStyle}>
            <option value="">{$t('invoiceCreate.paymentMethods.none')}</option>
            {#each CONDIZIONI_CODES as code}
              <option value={code}>{$t(`invoiceCreate.paymentConditions.${code}`)}</option>
            {/each}
          </select>
        </label>
        <label class="block">
          <span id="{fid}-8" class={labelCls} style={labelStyle}>{$t('invoiceCreate.fields.method')}</span>
          <select aria-labelledby="{fid}-8" bind:value={form.pagamento.modalitaPagamento} class={inputCls} style={inputStyle}>
            <option value="">{$t('invoiceCreate.paymentMethods.none')}</option>
            {#each modalitaOptions(form.pagamento.modalitaPagamento) as code}
              <option value={code}>{$t(`invoiceCreate.paymentMethods.${code}`, undefined, code)}</option>
            {/each}
          </select>
        </label>
        <label class="block">
          <span id="{fid}-9" class={labelCls} style={labelStyle}>{$t('invoiceCreate.fields.iban')}</span>
          <input aria-labelledby="{fid}-9" bind:value={form.pagamento.iban} maxlength="34" class={inputCls} style={inputStyle} />
        </label>
        <label class="block">
          <span id="{fid}-10" class={labelCls} style={labelStyle}>{$t('invoices.correction.fields.dataScadenza')}</span>
          <input aria-labelledby="{fid}-10" type="date" bind:value={form.pagamento.dataScadenza} class={inputCls} style={inputStyle} />
        </label>
      </div>

      {#if form.pagamento.dettagli.length > 0}
        <p class="eyebrow mt-4 mb-2" style="color: var(--color-text-muted);">
          {$t('invoices.correction.instalmentsTitle', { n: form.pagamento.dettagli.length })}
        </p>
        {#if modalitaMissing.length > 0}
          <p class="mb-2 text-xs measure text-warning" role="status">
            {$t('invoices.correction.instalmentsModalitaMissing', { rate: modalitaMissing.join(', ') })}
          </p>
        {/if}
        <div class="space-y-3">
          {#each form.pagamento.dettagli as rata, i}
            <div class="grid grid-cols-1 md:grid-cols-4 gap-3 border-t pt-3" style="border-color: var(--color-tint-soft);">
              <div class="flex items-end">
                <span class="text-sm font-mono" style="color: var(--color-text-muted);">
                  {$t('invoices.correction.instalmentNumber', { n: i + 1 })}
                </span>
              </div>
              <label class="block">
                <span id="{fid}-11-{i}" class={labelCls} style={labelStyle}>{$t('invoiceCreate.fields.method')}</span>
                <select aria-labelledby="{fid}-11-{i}" bind:value={rata.modalitaPagamento} class={inputCls} style={inputStyle}>
                  <option value="">{$t('invoices.correction.fields.inheritModalita')}</option>
                  {#each modalitaOptions(rata.modalitaPagamento) as code}
                    <option value={code}>{$t(`invoiceCreate.paymentMethods.${code}`, undefined, code)}</option>
                  {/each}
                </select>
              </label>
              <label class="block">
                <span id="{fid}-12-{i}" class={labelCls} style={labelStyle}>{$t('invoices.correction.fields.importo')}</span>
                <input aria-labelledby="{fid}-12-{i}" bind:value={rata.importoPagamento} inputmode="decimal" class={inputCls} style={inputStyle} />
              </label>
              <label class="block">
                <span id="{fid}-13-{i}" class={labelCls} style={labelStyle}>{$t('invoices.correction.fields.dataScadenza')}</span>
                <input aria-labelledby="{fid}-13-{i}" type="date" bind:value={rata.dataScadenza} class={inputCls} style={inputStyle} />
              </label>
              {#each classified.instalments[i] || [] as issue}
                <p class="md:col-span-4 text-xs measure text-warning" role="status">{issue}</p>
              {/each}
            </div>
          {/each}
        </div>
      {/if}

      <div class="flex flex-wrap items-center gap-3 mt-6">
        <button type="submit" disabled={saving}
          class="btn btn-primary btn-sm gap-2">
          <Save size={14} />
          {saving ? $t('invoices.correction.saving') : $t('invoices.correction.save')}
        </button>
        <button type="button" on:click={() => dispatch('close')}
          class="btn btn-secondary btn-sm">
          {$t('common.cancel')}
        </button>
        <span class="text-xs" style="color: var(--color-text-muted);">
          {changedFields.length === 0
            ? $t('invoices.correction.noChangesYet')
            : $t('invoices.correction.changedCount', { n: changedFields.length })}
        </span>
      </div>
    </form>
  </div>
{/if}
